import { ref, push, set, serverTimestamp } from 'firebase/database';
import { db } from '../../../integrations/firebase';

/**
 * ChatAuditService — Append-only audit log for critical chat actions.
 * Writes to: chat_audit_logs/{chatId}/{logId}
 * 
 * Frontend NEVER updates or deletes existing audit entries.
 * Firebase rules enforce append-only.
 */
export const ChatAuditService = {
    /**
     * Log an auditable action.
     * @param {string} chatId
     * @param {Object} entry
     * @param {string} entry.action - e.g. 'message_deleted', 'member_added'
     * @param {string} entry.actorId - UID of who performed the action
     * @param {string} entry.actorName - Display name
     * @param {string} [entry.targetId] - UID or messageId of target
     * @param {*} [entry.before] - Previous state (for edits/changes)
     * @param {*} [entry.after] - New state
     * @param {Object} [entry.metadata] - Extra context
     */
    log: async (chatId, entry) => {
        if (!chatId || !entry?.action || !entry?.actorId) {
            console.warn('[ChatAudit] Missing required fields:', { chatId, entry });
            return;
        }

        try {
            const logRef = push(ref(db, `chat_audit_logs/${chatId}`));
            await set(logRef, {
                action: entry.action,
                actorId: entry.actorId,
                actorName: entry.actorName || '',
                targetId: entry.targetId || null,
                before: entry.before || null,
                after: entry.after || null,
                metadata: entry.metadata || null,
                timestamp: serverTimestamp(),
                clientTimestamp: Date.now()
            });
        } catch (error) {
            // Audit failures should never block the main operation
            console.error('[ChatAudit] Failed to write audit log:', error);
        }
    },

    // Convenience methods for common actions
    logMessageDeleted: (chatId, { actorId, actorName, messageId, deleteType, messagePreview }) =>
        ChatAuditService.log(chatId, {
            action: 'message_deleted',
            actorId,
            actorName,
            targetId: messageId,
            before: messagePreview ? { text: messagePreview } : null,
            after: { deleteType }
        }),

    logMessageEdited: (chatId, { actorId, actorName, messageId, oldText, newText }) =>
        ChatAuditService.log(chatId, {
            action: 'message_edited',
            actorId,
            actorName,
            targetId: messageId,
            before: { text: oldText },
            after: { text: newText }
        }),

    logMessagePinned: (chatId, { actorId, actorName, messageId, messagePreview }) =>
        ChatAuditService.log(chatId, {
            action: 'message_pinned',
            actorId,
            actorName,
            targetId: messageId,
            after: { text: messagePreview }
        }),

    logMessageUnpinned: (chatId, { actorId, actorName, messageId }) =>
        ChatAuditService.log(chatId, {
            action: 'message_unpinned',
            actorId,
            actorName,
            targetId: messageId
        }),

    logGroupCreated: (chatId, { actorId, actorName, groupName, memberCount }) =>
        ChatAuditService.log(chatId, {
            action: 'group_created',
            actorId,
            actorName,
            after: { groupName, memberCount }
        }),

    logMemberAdded: (chatId, { actorId, actorName, memberId, memberName }) =>
        ChatAuditService.log(chatId, {
            action: 'member_added',
            actorId,
            actorName,
            targetId: memberId,
            after: { memberName }
        }),

    logMemberRemoved: (chatId, { actorId, actorName, memberId, memberName }) =>
        ChatAuditService.log(chatId, {
            action: 'member_removed',
            actorId,
            actorName,
            targetId: memberId,
            before: { memberName }
        }),

    logAdminPromoted: (chatId, { actorId, actorName, memberId }) =>
        ChatAuditService.log(chatId, {
            action: 'admin_promoted',
            actorId,
            actorName,
            targetId: memberId,
            before: { role: 'member' },
            after: { role: 'admin' }
        }),

    logAdminDemoted: (chatId, { actorId, actorName, memberId }) =>
        ChatAuditService.log(chatId, {
            action: 'admin_demoted',
            actorId,
            actorName,
            targetId: memberId,
            before: { role: 'admin' },
            after: { role: 'member' }
        }),

    logOwnershipTransferred: (chatId, { actorId, actorName, newOwnerId, newOwnerName }) =>
        ChatAuditService.log(chatId, {
            action: 'ownership_transferred',
            actorId,
            actorName,
            targetId: newOwnerId,
            before: { ownerId: actorId },
            after: { ownerId: newOwnerId, ownerName: newOwnerName }
        }),

    logGroupInfoChanged: (chatId, { actorId, actorName, field, before, after }) =>
        ChatAuditService.log(chatId, {
            action: 'group_info_changed',
            actorId,
            actorName,
            metadata: { field },
            before: { [field]: before },
            after: { [field]: after }
        })
};
